import React, {
  useEffect,
  useMemo,
} from 'react';
import Table from 'core/components/VirtualTable';
import {
  ServiceAllocation,
  VisitInfo,
} from 'pages/Dashboard/pages/Billing/types';
import {
  claimActionsColumn,
  claimHeaderColumns,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/columns';
import TableRow from 'core/components/VirtualTable/Row';
import clsx from 'clsx';
import {
  useSelectableContext,
} from 'core/hooks';
import VisitBreakdownView from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/VisitBreakdownView';
import {
  UpdateDataCallback,
} from 'core/components/VirtualTable/types';
import {
  Row,
} from '@tanstack/react-table';
import {
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import {
  isServiceLineInactive,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/serviceLineStatus';

type Props = {
  data: VisitInfo[];
  update?: UpdateDataCallback<ServiceAllocation>;
  fetchingBillingData: boolean;
};

export const isClaimExpandable = (data: VisitInfo) => (data?.allocation ?? []).length > 0;

function getClaimRowId(row: VisitInfo, index: number) {
  return [
    row?.claim?.claimId ?? row?.claimId ?? 0,
    row?.encounterId ?? 0,
    row?.visitDate ?? '',
    index,
  ].join('-');
}

type ServiceBreakdownRowProps = {
  row: Row<VisitInfo>;
  update?: UpdateDataCallback<ServiceAllocation>;
};

function ServiceBreakdownRow({
  row,
  update,
}: ServiceBreakdownRowProps) {
  const { isSelected, add, remove } = useSelectableContext();
  const expanded = isSelected(row?.original?.claim?.claimId ?? '');
  const data = row?.original ?? {};
  const claimId = data?.claim?.claimId ?? '';
  return (
    <>
      <TableRow<VisitInfo>
        row={row}
        className="!justify-start !items-stretch"
        cellClassName={clsx('!h-auto !items-center')}
        onClick={(row: { original: VisitInfo }) => {
          if (isClaimExpandable(row?.original) && !isSelected(claimId)) {
            add(claimId);
          } else if (isSelected(claimId)) {
            remove(claimId);
          }
        }}
      />
      {expanded && (
        <div className="w-full">
          <VisitBreakdownView
            key={row.id}
            readonly={data?.isShiftedToPatient ?? false}
            data={data}
            update={update}
          />
        </div>
      )}
    </>
  );
}

export default function ClaimVisitSummary({
  data,
  update,
  fetchingBillingData,
}: Props) {
  const { add } = useSelectableContext();
  const { watch } = useFormContext();
  const eobId = watch('eob.eobId');

  useEffect(() => {
    const claimIds = compact(
    data
      ?.filter((visit) =>
        (visit?.allocation ?? []).some(
          (allocation) =>
            allocation?.eobId === eobId
            && !isServiceLineInactive(allocation),
        ),
      )
      .map((visit) => visit?.claim?.claimId ?? ''),
    );
    if (!fetchingBillingData && claimIds?.length > 0) {
      add(...claimIds);
    }
  }, [fetchingBillingData]);

  const isNew = isNil(eobId);

  const columns = useMemo(
    () => (isNew
      ? claimHeaderColumns
      : [...claimHeaderColumns, claimActionsColumn]),
    [isNew],
  );

  return (
    <Table<VisitInfo>
      columns={columns}
      data={data}
      getRowId={getClaimRowId}
      classes={{
        header: '!items-end !text-gray-500 bg-gray-50 !py-0',
        outerRow: '!w-full',
        scrollbarGutter: 'inherit',
        windowsClassName: '!w-full',
      }}
      renderRowComponent={(row) => (
        <ServiceBreakdownRow row={row} update={update} />
      )}
    />
  );
}
